import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import {
  Download,
  History as HistoryIcon,
  Preview,
  Refresh,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import { formatHistoryUserName } from './reportHistoryUtils';
import ReportPreviewDialog from './ReportPreviewDialog';
import ReportVisualizeDialog from './ReportVisualizeDialog';
import {
  downloadAircraftLifecycleReportHistoryCsv,
  downloadAircraftLifecycleReportHistoryPdf,
  downloadAircraftLifecycleReportCsv,
  downloadAircraftLifecycleReportPdf,
  fetchAircraftLifecycleReportPreviewData,
  fetchAircraftLifecycleReportHistory,
  resolveAxiosErrorMessage
} from '../../utils/reportCenterApi';

function todayDateValue() {
  const d = new Date();
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function formatDisplayDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDisplayDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Dedicated Aircraft Life-cycle Management Report — assemblies and components
 * with list columns plus detail fields from the edit dialogs.
 */
const AircraftLifecycleReportPanel = ({ aircraftOptions = [], aircraftLabelById = {} }) => {
  const { showToast } = useToast();
  const [subTab, setSubTab] = useState(0);
  const [selectedAircraftIds, setSelectedAircraftIds] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reportDate, setReportDate] = useState(todayDateValue);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewReportData, setPreviewReportData] = useState(null);
  const [visualizeOpen, setVisualizeOpen] = useState(false);
  const [visualizeData, setVisualizeData] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await fetchAircraftLifecycleReportHistory();
      setHistory(rows);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (subTab === 1) {
      loadHistory();
    }
  }, [subTab, loadHistory]);

  const dateRangeError = useMemo(() => {
    if (dateFrom && dateTo && dateFrom > dateTo) {
      return 'Date From must be on or before Date To';
    }
    return '';
  }, [dateFrom, dateTo]);

  const fileStamp = dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : reportDate;

  const reportPayload = useMemo(() => ({
    aircraftIds: selectedAircraftIds,
    includeArchived,
    search,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    reportDate
  }), [selectedAircraftIds, includeArchived, search, dateFrom, dateTo, reportDate]);

  const aircraftRequired = selectedAircraftIds.length === 0;

  const handlePreview = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    if (aircraftRequired) {
      showToast('Select at least one aircraft', 'warning');
      return;
    }
    setPreviewing(true);
    try {
      const reportData = await fetchAircraftLifecycleReportPreviewData(reportPayload);
      setPreviewReportData(reportData);
      setPreviewOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Preview failed', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleVisualize = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    if (aircraftRequired) {
      showToast('Select at least one aircraft', 'warning');
      return;
    }
    setVisualizing(true);
    try {
      const reportData = await fetchAircraftLifecycleReportPreviewData(reportPayload);
      setVisualizeData(reportData);
      setVisualizeOpen(true);
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Visualization failed', 'error');
    } finally {
      setVisualizing(false);
    }
  };

  const handleDownload = async (visualizations) => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    if (aircraftRequired) {
      showToast('Select at least one aircraft', 'warning');
      return;
    }
    setDownloading(true);
    try {
      await downloadAircraftLifecycleReportPdf(
        { ...reportPayload, visualizations },
        `Aircraft_Lifecycle_Report_${fileStamp}.pdf`
      );
      showToast(
        visualizations?.length
          ? 'Aircraft Life-cycle Report with visualizations downloaded and saved to history'
          : 'Aircraft Life-cycle Report downloaded and saved to history',
        'success'
      );
      if (subTab === 1) {
        loadHistory();
      }
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'Download failed', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    if (dateRangeError) {
      showToast(dateRangeError, 'warning');
      return;
    }
    if (aircraftRequired) {
      showToast('Select at least one aircraft', 'warning');
      return;
    }
    setDownloadingCsv(true);
    try {
      await downloadAircraftLifecycleReportCsv(
        reportPayload,
        `Aircraft_Lifecycle_Report_${fileStamp}.pdf`
      );
      showToast('Aircraft Life-cycle Report CSV downloaded', 'success');
    } catch (err) {
      showToast(await resolveAxiosErrorMessage(err) || 'CSV download failed', 'error');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const handleHistoryDownload = (row) => {
    try {
      downloadAircraftLifecycleReportHistoryPdf(row._id, row.filename || 'Aircraft_Lifecycle_Report.pdf');
      showToast('Re-downloading stored PDF', 'success');
    } catch (err) {
      showToast(err.message || 'Re-download failed', 'error');
    }
  };

  const handleHistoryDownloadCsv = (row) => {
    try {
      downloadAircraftLifecycleReportHistoryCsv(row._id, row.filename || 'Aircraft_Lifecycle_Report.pdf');
      showToast('Downloading CSV', 'success');
    } catch (err) {
      showToast(err.message || 'CSV download failed', 'error');
    }
  };

  const busy = previewing || downloading || downloadingCsv || visualizing;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Aircraft Life-cycle Management Report
      </Typography>
      <Typography color="text.secondary" paragraph>
        Assemblies and components for selected aircraft — list columns plus detail fields from the
        Life-cycle edit dialogs (installation, ATA, work order, notes, time since records, threshold /
        frequency, and remaining). Matches the Aircraft Life-cycle Management page scope.
      </Typography>

      <Tabs
        value={subTab}
        onChange={(_, v) => setSubTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Generate" />
        <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
      </Tabs>

      {subTab === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5} maxWidth={640}>
            <FormControl fullWidth required error={aircraftRequired}>
              <InputLabel id="aircraft-lifecycle-report-aircrafts-label">Aircrafts *</InputLabel>
              <Select
                labelId="aircraft-lifecycle-report-aircrafts-label"
                multiple
                value={selectedAircraftIds}
                label="Aircrafts *"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedAircraftIds(typeof value === 'string' ? value.split(',') : value);
                }}
                renderValue={(selected) => {
                  if (!selected.length) return 'Select aircraft';
                  return selected.map((id) => aircraftLabelById[id] || id).join(', ');
                }}
              >
                {aircraftOptions.map((ac) => (
                  <MenuItem key={ac.value} value={ac.value}>
                    {ac.label}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                Required — same as Aircraft Life-cycle Management
              </Typography>
            </FormControl>

            <TextField
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              placeholder="Type, serial, part number, description…"
              helperText="Filters both Assemblies and Components tables"
            />

            <FormControlLabel
              control={(
                <Switch
                  checked={includeArchived}
                  onChange={(e) => setIncludeArchived(e.target.checked)}
                />
              )}
              label="Include archived / inactive records"
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Date from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
              <TextField
                label="Date to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank for all records"
              />
            </Stack>

            {dateRangeError && (
              <Alert severity="warning">{dateRangeError}</Alert>
            )}

            <TextField
              label="Report Date"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={visualizing ? <CircularProgress size={16} /> : <BarChartIcon />}
                onClick={handleVisualize}
                disabled={busy || aircraftRequired || Boolean(dateRangeError)}
              >
                Visualize
              </Button>
              <Button
                variant="outlined"
                startIcon={previewing ? <CircularProgress size={16} /> : <Preview />}
                onClick={handlePreview}
                disabled={busy || aircraftRequired || Boolean(dateRangeError)}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <Download />}
                onClick={() => handleDownload()}
                disabled={busy || aircraftRequired || Boolean(dateRangeError)}
              >
                Download PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={downloadingCsv ? <CircularProgress size={16} /> : <Download />}
                onClick={handleDownloadCsv}
                disabled={busy || aircraftRequired || Boolean(dateRangeError)}
              >
                Download CSV
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}

      {subTab === 1 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1">Downloaded Aircraft Life-cycle Reports</Typography>
            <Button
              size="small"
              startIcon={historyLoading ? <CircularProgress size={14} /> : <Refresh />}
              onClick={loadHistory}
              disabled={historyLoading}
            >
              Refresh
            </Button>
          </Box>

          {historyLoading && history.length === 0 ? (
            <Box py={4} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : history.length === 0 ? (
            <Alert severity="info">
              No Aircraft Life-cycle Report downloads yet. Generate one from the Generate tab.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Downloaded</TableCell>
                  <TableCell>Generated by</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Aircrafts</TableCell>
                  <TableCell>Search</TableCell>
                  <TableCell>Archived</TableCell>
                  <TableCell align="right">Rows</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDisplayDateTime(row.downloadedAt)}</TableCell>
                    <TableCell>{formatHistoryUserName(row.downloadedBy)}</TableCell>
                    <TableCell>
                      {row.dateFrom || row.dateTo
                        ? `${formatDisplayDate(row.dateFrom)} – ${formatDisplayDate(row.dateTo)}`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {(row.aircraftLabels || []).length
                        ? row.aircraftLabels.join(', ')
                        : '—'}
                    </TableCell>
                    <TableCell>{row.search || '—'}</TableCell>
                    <TableCell>{row.includeArchived ? 'Included' : 'Active only'}</TableCell>
                    <TableCell align="right">{row.rowCount ?? '—'}</TableCell>
                    <TableCell align="right">{formatFileSize(row.fileSize)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownload(row)}
                        >
                          PDF
                        </Button>
                        <Button
                          size="small"
                          startIcon={<Download />}
                          onClick={() => handleHistoryDownloadCsv(row)}
                        >
                          CSV
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <ReportPreviewDialog
        open={previewOpen}
        title="Aircraft Life-cycle Report preview"
        reportData={previewReportData}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewReportData(null);
        }}
        onDownload={() => handleDownload()}
        downloading={downloading}
        onDownloadCsv={handleDownloadCsv}
        downloadingCsv={downloadingCsv}
      />

      <ReportVisualizeDialog
        open={visualizeOpen}
        title="Aircraft Life-cycle Report"
        data={visualizeData}
        downloading={downloading}
        onClose={() => {
          setVisualizeOpen(false);
          setVisualizeData(null);
        }}
        onDownloadPdfWithVisualization={(visualizations) => handleDownload(visualizations)}
      />
    </Box>
  );
};

export default AircraftLifecycleReportPanel;
